import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check, Airplane, Suitcase, UserCircle, ArrowRight, WhatsappLogo, Envelope } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GreetMeetPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", service_type: "greet_meet", message: "", preferred_contact: "email",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success(t("greet_meet.quote_desc"));
        setFormData({ name: "", email: "", phone: "", service_type: "greet_meet", message: "", preferred_contact: "email" });
      }
    } catch (error) {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const features = [
    { icon: Airplane, title: t("greet_meet.features_1_title"), description: t("greet_meet.features_1_desc") },
    { icon: Suitcase, title: t("greet_meet.features_2_title"), description: t("greet_meet.features_2_desc") },
    { icon: UserCircle, title: t("greet_meet.features_3_title"), description: t("greet_meet.features_3_desc") },
  ];

  const services = [
    t("greet_meet.service_1"), t("greet_meet.service_2"), t("greet_meet.service_3"),
    t("greet_meet.service_4"), t("greet_meet.service_5"), t("greet_meet.service_6"),
  ];

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="greet-meet-page">
      <SEO title="Meet & Greet — Accueil en Aéroport" path="/services/greet-meet" description="Service Meet & Greet ALTURA : accueil personnalisé en aéroport et gare à Paris. Pancarte nominative, prise en charge des bagages, 1h d'attente incluse en aéroport." />
      <section className="relative h-[60vh] min-h-[450px] flex items-end">
        <div className="absolute inset-0">
          <img
            src="/images/greet_meet_altura.png"
            alt="Meet & Greet ALTURA"
            className="w-full h-full object-cover object-center"
          />
          <div className="hero-overlay-greet absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 w-full pb-10 sm:pb-14">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-md">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">{t("greet_meet.hero_subtitle")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-[#1A1A1A] leading-none">
              {t("greet_meet.hero_title_1")} <span className="italic">{t("greet_meet.hero_title_2")}</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#E5E3DC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-[#1A1A1A]/10 p-8 text-center">
                <feature.icon size={40} weight="thin" className="text-[#1A1A1A] mx-auto mb-6" />
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-4">{feature.title}</h3>
                <p className="text-[#1A1A1A]/70 font-light text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EDEBE4]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl lg:text-3xl text-[#1A1A1A] mb-8">{t("greet_meet.services_title")}</h2>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">{t("greet_meet.services_desc")}</p>
              <ul className="space-y-4 mb-12">
                {services.map((service, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <Check size={20} weight="bold" className="text-[#1A1A1A] mt-0.5 flex-shrink-0" />
                    <span className="text-[#1A1A1A]/80 font-light">{service}</span>
                  </li>
                ))}
              </ul>
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-6">{t("greet_meet.airports_title")}</h3>
              <div className="grid grid-cols-2 gap-4">
                {["Paris CDG", "Paris Orly", "Gare du Nord", "Gare de Lyon"].map((location) => (
                  <div key={location} className="bg-white border border-[#1A1A1A]/10 p-4 text-center">
                    <p className="text-[#1A1A1A]/80 text-sm">{location}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="bg-white border border-[#1A1A1A]/10 p-8 lg:p-12">
                <h2 className="font-heading text-2xl text-[#1A1A1A] mb-2">{t("greet_meet.quote_title")}</h2>
                <p className="text-[#1A1A1A]/70 text-sm mb-8">{t("greet_meet.quote_desc")}</p>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="greet-meet-form">
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("greet_meet.name_label")} *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("greet_meet.name_placeholder")} data-testid="input-name" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("greet_meet.email_label")} *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("greet_meet.email_placeholder")} data-testid="input-email" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("greet_meet.phone_label")}</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="input-luxury w-full bg-transparent" placeholder={t("greet_meet.phone_placeholder")} data-testid="input-phone" />
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("greet_meet.contact_pref")}</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="preferred_contact" value="email" checked={formData.preferred_contact === "email"} onChange={handleChange} className="accent-[#1A1A1A]" />
                        <Envelope size={18} className="text-[#1A1A1A]/60" />
                        <span className="text-[#1A1A1A]/80 text-sm">{t("common.email")}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="preferred_contact" value="whatsapp" checked={formData.preferred_contact === "whatsapp"} onChange={handleChange} className="accent-[#1A1A1A]" />
                        <WhatsappLogo size={18} className="text-[#1A1A1A]/60" />
                        <span className="text-[#1A1A1A]/80 text-sm">{t("common.whatsapp")}</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("greet_meet.message_label")} *</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className="input-luxury w-full bg-transparent resize-none" placeholder={t("greet_meet.message_placeholder")} data-testid="input-message" />
                  </div>
                  <button type="submit" disabled={loading} className="btn-dark w-full flex items-center justify-center gap-3 disabled:opacity-50" data-testid="submit-contact">
                    {loading ? t("greet_meet.submitting") : t("greet_meet.submit")}
                    <ArrowRight size={16} weight="bold" />
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default GreetMeetPage;
